/**
 * Vue Router Configuration
 */

import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'

// Extend RouteMeta type
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    title?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false, title: 'Anmelden' },
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/views/AuthCallbackView.vue'),
    meta: { requiresAuth: false, title: 'Anmeldung...' },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, title: 'Meine Kurse' },
  },
  {
    path: '/course/:id',
    name: 'CourseDetail',
    component: () => import('@/views/CourseDetailView.vue'),
    meta: { requiresAuth: true, title: 'Kursdetails' },
    props: true,
  },
  {
    path: '/course/:courseId/session/:sessionId/attendance',
    name: 'Attendance',
    component: () => import('@/views/AttendanceView.vue'),
    meta: { requiresAuth: true, title: 'Anwesenheit' },
    props: true,
  },
  {
    path: '/participants',
    name: 'ParticipantsList',
    component: () => import('@/views/ParticipantsListView.vue'),
    meta: { requiresAuth: true, title: 'Alle Teilnehmer' },
  },
  {
    path: '/participant/:id/:courseId?',
    name: 'ParticipantDetail',
    component: () => import('@/views/ParticipantDetailView.vue'),
    meta: { requiresAuth: true, title: 'Teilnehmer' },
    props: true,
  },
  {
    path: '/billing',
    name: 'Billing',
    component: () => import('@/views/BillingView.vue'),
    meta: { requiresAuth: true, title: 'Abrechnung' },
  },
  {
    path: '/billing/:id',
    name: 'BillingDetail',
    component: () => import('@/views/BillingDetailView.vue'),
    meta: { requiresAuth: true, title: 'Abrechnungsdetails' },
    props: true,
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: { requiresAuth: true, title: 'Profil' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { requiresAuth: false, title: 'Nicht gefunden' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Navigation Guards (return-based, no next() callback)
router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  // Update document title
  document.title = to.meta.title ? `${to.meta.title} | TusCourses` : 'TusCourses'

  // Check if route requires authentication
  if (to.meta.requiresAuth !== false) {
    if (!authStore.isAuthenticated) {
      authStore.setRedirectUrl(to.fullPath)
      return { name: 'Login' }
    }

    // Validate session if token exists but might be expired.
    // Skip validation when offline — token stays valid until expiry;
    // validation will happen on the next navigation while online.
    if (authStore.shouldValidateSession && navigator.onLine) {
      const isValid = await authStore.validateSession()
      if (!isValid) {
        authStore.setRedirectUrl(to.fullPath)
        return { name: 'Login' }
      }
    }
  }

  // Redirect authenticated users away from login
  if (to.name === 'Login' && authStore.isAuthenticated) {
    return { name: 'Dashboard' }
  }
})

// Catch chunk load errors (lazy-imported route components that can't be fetched).
// Online: chunk filenames changed after a new deployment → hard reload to get fresh assets.
// Offline: chunk was never loaded → can't navigate, inform user via custom event.
router.onError((error, to) => {
  const isChunkLoadError =
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Failed to load') ||
    error.message.includes('Importing a module script failed')

  if (!isChunkLoadError) return

  if (navigator.onLine) {
    window.location.href = to.fullPath
    return
  }

  // Offline: dispatch event so App.vue can show a toast
  console.warn(`[Router] Seite "${to.path}" offline nicht verfügbar (Chunk nicht geladen)`)
  window.dispatchEvent(
    new CustomEvent('chunk-load-error-offline', {
      detail: { path: to.path },
    })
  )
})

// ---------------------------------------------------------------------------
// Route Prefetch — load all lazy route chunks after initial navigation.
// Ensures offline SPA navigation works even without SW (dev mode) by putting
// every route component into the JS module cache before the user goes offline.
// In production this is a belt-and-suspenders backup to SW precaching.
// ---------------------------------------------------------------------------
router.isReady().then(() => {
  setTimeout(() => {
    router.getRoutes().forEach((route) => {
      const component = route.components?.['default']
      if (typeof component === 'function') {
        ;(component as () => Promise<unknown>)().catch(() => {
          // Silently ignore — chunk will be retried when user navigates
        })
      }
    })
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[Router] Route chunks prefetched for offline navigation')
    }
  }, 2000)
})

export default router
